import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import CasdoorSDK from 'casdoor-js-sdk';
import axios from 'axios';
import api from '../services/api'; // 导入已配置的api实例

// 用于跟踪正在处理的回调请求
const pendingCallbacks = new Map();

// Casdoor配置
const casdoorConfig = {
  serverUrl: "http://casdoor:8000",
  clientId: "74c38d7013568a6dc8df",
  appName: "tw_crm",
  organizationName: "tw_crm",
  redirectPath: "/callback",  // 回调路径（无需dashboard前缀了）
};

// 初始化Casdoor SDK
const sdk = new CasdoorSDK({
  serverUrl: casdoorConfig.serverUrl,
  clientId: casdoorConfig.clientId,
  appName: casdoorConfig.appName,
  organizationName: casdoorConfig.organizationName,
  redirectPath: casdoorConfig.redirectPath  // SDK会处理完整URL
});

// 创建专用于认证的axios实例
const authAxios = axios.create({
  baseURL: '',  // 使用相对路径
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// 创建认证上下文
const AuthContext = createContext(null);

// 认证提供器组件
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authType, setAuthType] = useState('standard'); // 认证类型，standard或wework
  const [tenant, setTenant] = useState(null); // 当前租户信息
  const [isAdmin, setIsAdmin] = useState(false); // 是否是系统管理员
  // 添加一个ref来追踪是否已处理过回调，防止重复处理
  const callbackProcessed = useRef(false);
  // 添加一个ref来存储处理中的请求promise
  const callbackPromiseRef = useRef(null);

  // 初始化时检查本地存储的token
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const tenantData = localStorage.getItem('tenant'); // 获取租户信息
    const savedAuthType = localStorage.getItem('authType') || 'standard';
    const isAdminFlag = localStorage.getItem('isAdmin') === 'true'; // 获取管理员标志
    
    if (token && user) {
      setIsAuthenticated(true);
      setUser(JSON.parse(user));
      setAuthType(savedAuthType);
      setIsAdmin(isAdminFlag); // 设置管理员状态
      if (tenantData) {
        setTenant(JSON.parse(tenantData)); // 设置租户信息
      }
    }
    
    // 检查URL中是否有code和state参数(Casdoor回调)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    // 只有当未处理过且有code和state时才处理回调
    if (code && state && !callbackProcessed.current && !callbackPromiseRef.current) {
      console.log('首次处理OAuth回调 - 组件挂载'); 
      callbackProcessed.current = true; // 标记为已处理
      
      // 如果已经存在全局处理中的相同code请求，复用它
      if (pendingCallbacks.has(code)) {
        console.log('复用已存在的全局回调处理');
        callbackPromiseRef.current = pendingCallbacks.get(code);
      } else {
        // 否则创建新的处理
        callbackPromiseRef.current = handleCasdoorCallback(code, state);
      }
    }
    
    setLoading(false);
    
    // 当组件卸载时，清除状态
    return () => {
      callbackProcessed.current = false;
      callbackPromiseRef.current = null;
    };
  }, []);

  // 处理Casdoor回调
  const handleCasdoorCallback = async (code, state) => {
    // 如果已经有相同的code正在处理，复用那个Promise
    if (pendingCallbacks.has(code)) {
      console.log('已有相同code的请求正在全局处理中，跳过重复请求');
      return pendingCallbacks.get(code);
    }
    
    // 创建新的处理Promise
    const processPromise = (async () => {
      try {
        setLoading(true);
        console.log('🔑 开始处理OAuth回调，code:', code.substring(0, 5) + '...', 'state:', state);
        
        // 添加超时处理
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('认证请求超时')), 15000);
        });
        
        // 记录localStorage中的当前状态
        console.log('💾 认证前本地存储状态:', {
          token: !!localStorage.getItem('token'),
          user: !!localStorage.getItem('user'),
          tenant: !!localStorage.getItem('tenant')
        });
        
        // 使用完整的URL路径，避免路径拼接问题
        console.log('📤 准备发送callback请求到后端');
        const response = await Promise.race([
          api.get(`/auth/callback?code=${code}&state=${state}`), // 使用配置好的api实例，而不是authAxios
          timeoutPromise
        ]);
        
        // 检查响应是否有效
        console.log('📥 收到后端callback响应:', {
          status: response.status,
          hasToken: !!response.data?.token,
          hasUsername: !!response.data?.username,
        });
        
        if (!response || !response.data) {
          console.error('认证响应无效:', response);
          throw new Error('服务器响应无效');
        }
        
        const { 
          token, 
          username, 
          displayName, 
          avatar, 
          authType: responseAuthType,
          tenantId,
          tenantCode,
          tenantName,
          roles,
          isAdmin: responseIsAdmin
        } = response.data;
        
        // 验证必要字段
        if (!token || !username) {
          console.error('认证响应缺少必要字段:', response.data);
          throw new Error('认证信息不完整');
        }
        
        // 保存认证信息和租户信息
        console.log('💾 准备保存认证信息:', { username, hasToken: !!token });
        const userData = { username, displayName, avatar, roles };
        const tenantData = { id: tenantId, code: tenantCode, name: tenantName };
        login(token, userData, tenantData, responseAuthType || 'standard', responseIsAdmin);
        console.log('✅ 认证成功，已保存用户信息:', username);
        
        // 记录localStorage中的更新状态
        console.log('💾 认证后本地存储状态:', {
          token: !!localStorage.getItem('token'),
          tokenLength: localStorage.getItem('token')?.length || 0,
          user: !!localStorage.getItem('user'),
          tenant: !!localStorage.getItem('tenant')
        });
        
        // 不再在此处清除URL参数，让Callback组件处理重定向
        
        return true; // 返回成功状态
      } catch (error) {
        console.error('❌ 认证回调处理失败:', error);
        // 提供更具体的错误消息
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        console.error('❌ 认证错误详情:', errorMessage);
        
        // 检查是否是后端返回的详细错误信息
        if (error.response?.data?.error) {
          console.error('❌ 后端错误信息:', error.response.data.error);
        }
        
        // 尝试诊断连接问题
        console.log('🔍 尝试检查认证服务状态...');
        try {
          const statusCheck = await api.get('/auth/status');
          console.log('📊 认证服务状态:', statusCheck.data);
        } catch (statusError) {
          console.error('❌ 无法获取认证服务状态:', statusError.message);
        }
        
        // 如果有错误，确保清除任何可能部分设置的认证状态
        // 使用清除函数而非logout以避免重定向循环
        clearAuthState();
        return false; // 返回失败状态
      } finally {
        setLoading(false);
        // 处理完成后，从Map中移除
        pendingCallbacks.delete(code);
      }
    })();
    
    // 将Promise存入Map
    pendingCallbacks.set(code, processPromise);
    
    return processPromise;
  };

  // 清除认证状态而不重定向的辅助函数
  const clearAuthState = () => {
    console.log('🧹 清除认证状态');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('authType');
    localStorage.removeItem('isAdmin');
    setIsAuthenticated(false);
    setUser(null);
    setTenant(null);
    setAuthType('standard');
    setIsAdmin(false);
  };

  // 登录函数
  const login = (token, userData, tenantData, loginAuthType = 'standard', userIsAdmin = false) => {
    console.log('💿 保存认证信息到localStorage', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      username: userData?.username,
      authType: loginAuthType
    });
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('tenant', JSON.stringify(tenantData)); // 保存租户信息
    localStorage.setItem('authType', loginAuthType);
    localStorage.setItem('isAdmin', userIsAdmin.toString()); // 保存管理员标志
    
    // 验证token是否实际保存成功
    const savedToken = localStorage.getItem('token');
    console.log('🔍 验证token是否保存成功:', { 
      正确保存: !!savedToken, 
      长度: savedToken?.length || 0 
    });
    
    setIsAuthenticated(true);
    setUser(userData);
    setTenant(tenantData); // 设置租户状态
    setAuthType(loginAuthType);
    setIsAdmin(userIsAdmin); // 设置管理员状态
  };

  // 获取Casdoor登出URL的辅助函数
  const getSignOutUrl = () => {
    try {
      // 尝试使用SDK方法
      if (typeof sdk.getSignOutUrl === 'function') {
        return sdk.getSignOutUrl();
      }
      
      // 如果SDK方法不存在，手动构建登出URL
      const appName = casdoorConfig.appName;
      const redirectUri = encodeURIComponent(window.location.origin);
      return `${casdoorConfig.serverUrl}/login/logout/${appName}?redirect_uri=${redirectUri}`;
    } catch (error) {
      console.error('生成登出URL失败:', error);
      return '/';
    }
  };

  // 退出登录函数
  const logout = (redirect = true) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant'); // 移除租户信息
    localStorage.removeItem('authType');
    localStorage.removeItem('isAdmin'); // 移除管理员标志
    setIsAuthenticated(false);
    setUser(null);
    setTenant(null); // 清除租户状态
    setAuthType('standard');
    setIsAdmin(false); // 清除管理员状态
    
    // 只有在指定重定向时才重定向到Casdoor登出页面
    if (redirect) {
      window.location.href = getSignOutUrl();
    }
  };

  // 获取Casdoor登录URL的辅助函数
  const getSignInUrl = () => {
    try {
      // 尝试使用SDK方法
      if (typeof sdk.getSignInUrl === 'function') {
        return sdk.getSignInUrl();
      }
      
      // 如果SDK方法不存在，手动构建登录URL
      const redirectUri = encodeURIComponent(`${window.location.origin}${casdoorConfig.redirectPath}`);
      const state = Math.random().toString(36).substring(2);
      return `${casdoorConfig.serverUrl}/login/oauth/authorize?client_id=${casdoorConfig.clientId}&response_type=code&redirect_uri=${redirectUri}&scope=read&state=${state}`;
    } catch (error) {
      console.error('生成登录URL失败:', error);
      return '/';
    }
  };

  // 标准登录（通过Casdoor的登录页面）
  const redirectToStandardLogin = () => {
    try {
      // 直接使用后端的登录API获取Casdoor登录URL
      api.get('/auth/login').then(response => {
        const { redirectUrl } = response.data;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          // 如果后端没有返回redirectUrl，使用辅助函数生成登录URL
          window.location.href = getSignInUrl();
        }
      }).catch(error => {
        console.error('获取登录URL失败:', error);
        // 如果API调用失败，使用辅助函数生成登录URL
        window.location.href = getSignInUrl();
      });
    } catch (error) {
      console.error('获取登录URL失败:', error);
      // 如果后端API调用失败，使用辅助函数生成登录URL
      window.location.href = getSignInUrl();
    }
  };

  // 企业微信登录
  const redirectToWeworkLogin = () => {
    try {
      // 调用后端企业微信登录API
      api.get('/auth/login/wework').then(response => {
        const { redirectUrl } = response.data;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      });
    } catch (error) {
      console.error('获取企业微信登录URL失败:', error);
    }
  };

  // 根据登录类型重定向到相应的登录页面
  const redirectToLogin = (loginType = 'standard') => {
    if (loginType === 'wework') {
      redirectToWeworkLogin();
    } else {
      redirectToStandardLogin();
    }
  };

  // 检查用户是否是系统管理员
  const isSystemAdmin = () => {
    return isAdmin;
  };

  // 提供认证状态和方法给子组件
  const value = {
    isAuthenticated,
    user,
    loading,
    authType,
    tenant, // 添加租户信息到上下文
    isSystemAdmin, // 添加系统管理员检查方法
    login,
    logout,
    redirectToLogin,
    redirectToStandardLogin,
    redirectToWeworkLogin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 自定义Hook，方便组件使用认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
}; 